import styled from 'styled-components';
import {
  Flex,
  isUserWithRoles,
  useCombinedUser,
  UserRoles,
  Tooltip,
} from '@equifinance/shared';
import { useState } from 'react';
import {
  Application, UnderwritingStatuses, RejectReasons,
  WithdrawnReasons,
} from '../../../contexts/applications';
import ChangeStatusModal from '../modals/ChangeStatusModal';

const EditIcon = styled.i`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
  cursor: pointer;
`}`;

const StatusColumnContainer = styled(Flex)`${({ theme }) => `
  width: 100%;
  max-width: 100%;
  gap: ${theme.padding[2]};
  align-items: center;
`}`;

const StatusBox = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[1]} ${theme.padding[2]};
  color: ${theme.colours.neutral[10]};
  gap: ${theme.padding[1]};
  border-radius: ${theme.padding[1]};
  font-weight: ${theme.fontWeight.bold};
  line-height: ${theme.fontSize[2]};
  align-items: center;
`}`;

const WithdrawnStatusBox = styled(StatusBox)`${({ theme }) => `
  background: ${theme.colours.neutral[4]};
`}`;

const CompleteStatusBox = styled(StatusBox)`${({ theme }) => `
  background: ${theme.colours.primary[4]};
`}`;

const RejectedStatusBox = styled(StatusBox)`${({ theme }) => `
  background: ${theme.colours.danger[1]};
  padding: ${theme.borderWidth[2]} ${theme.padding[2]};
  line-height: ${theme.fontSize[4]};

  > i {
    font-size: ${theme.fontSize[3]};
    color: ${theme.colours.neutral[10]};
  }
`}`;

const UnderwritingStatusText = styled.span``;

function UnderwritingStatus({ status, reason, id }: {
  status: UnderwritingStatuses | null,
  reason: string,
  id: string,
}) {
  if (status === UnderwritingStatuses.Rejected) {
    return (
      <RejectedStatusBox id={`rejected-${id}`}>
        <i className="mi-circle-error" />
        Rejected
        <Tooltip
          id={`rejected-${id}`}
          content={reason}
          place="bottom"
        />
      </RejectedStatusBox>
    );
  }
  if (status === UnderwritingStatuses.Withdrawn) {
    return (
      <WithdrawnStatusBox id={`withdrawn-${id}`}>
        Withdrawn
        <Tooltip
          id={`withdrawn-${id}`}
          content={reason}
          place="bottom"
        />
      </WithdrawnStatusBox>
    );
  }
  if (status === UnderwritingStatuses.Complete) {
    return (
      <CompleteStatusBox>
        Complete
      </CompleteStatusBox>
    );
  }
  return (
    <UnderwritingStatusText>
      {status || '-'}
    </UnderwritingStatusText>
  );
}

const defaultModal = { id: '', open: false };

export default function StatusColumn({ row }: { row: Application }) {
  const { user } = useCombinedUser();
  const [changeStatusModal, setChangeStatusModal] = useState<{ id: string, open: boolean }>(defaultModal);
  const canEdit = row.underwritingStatus
    && row.status === 'WithLender'
    && isUserWithRoles([UserRoles.CAN_CHANGE_APPLICATION_STATUS], user);

  const onOpenStatusEditModal = () => {
    setChangeStatusModal({
      open: true,
      id: row.id,
    });
  };

  return (
    <StatusColumnContainer>
      <UnderwritingStatus
        status={row.underwritingStatus}
        reason={
          row.underwritingStatus === UnderwritingStatuses.Rejected
            ? RejectReasons[row.rejectionReason as keyof typeof RejectReasons]
            : !['Other', null].includes(row.withdrawalReason)
              ? WithdrawnReasons[row.withdrawalReason as keyof typeof WithdrawnReasons]
              : row.otherWithdrawalDetail
        }
        id={row.id}
      />
      {canEdit && (
        <EditIcon className="mi-edit" onClick={onOpenStatusEditModal} />
      )}
      <span>
        <ChangeStatusModal
          applicationId={row.id}
          isOpen={changeStatusModal.open}
          onClose={() => setChangeStatusModal(defaultModal)}
        />
      </span>
    </StatusColumnContainer>
  );
}
